<template>
  <div class="">
    <label
      id="listbox-label"
      class="block text-sm leading-5 font-medium text-gray-700"
    >
      {{ label }}
    </label>
    <div class="relative z-20">
      <span class="inline-block w-full rounded-md shadow-sm">
        <button
          type="button"
          aria-haspopup="listbox"
          aria-expanded="true"
          aria-labelledby="listbox-label"
          class="cursor-pointer relative w-full rounded-md border border-gray-300 bg-white pl-3 pr-10 py-2 text-left focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition ease-in-out duration-150 sm:text-sm sm:leading-5"
          @click="dropdownOpen = !dropdownOpen"
        >
          <span class="w-full inline-flex space-x-2 truncate">
            <div v-if="multiSelect">
              <div
                v-for="item in selectedItems"
                :key="item"
                class="flex justify-center items-center border px-2 py-1 rounded-md text-white bg-gray-700 hover:bg-gray-600"
                @click="removeItem(item); dropdownOpen = !dropdownOpen"
              >
                <div>{{ item.primaryText }}</div>
                <div class="ml-2"><i class="fal fa-times" /></div>
              </div>
            </div>
            <div v-if="!multiSelect && displaySelectedItem[0] !== undefined">
              <span class="truncate">
                {{ displaySelectedItem[0].primaryText }}
              </span>
              <span class="text-gray-500 truncate ml-1">
                {{ displaySelectedItem[0].secondaryText }}
              </span>
            </div>

          </span>
          <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <svg
              class="h-5 w-5 text-gray-400"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
            >
              <path
                d="M7 7l3-3 3 3m0 6l-3 3-3-3"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>
        </button>
      </span>

      <div
        v-if="dropdownOpen"
        class="absolute w-full mt-1 bg-white shadow-lg rounded-md border"
      >
        <div
          v-if="allowSearch"
          class="flex items-center justify-between border-b border-gray-100 px-4 py-3 rounded-t-lg bg-gray-50 w-full min-w-full"
        >
          <input
            v-model="search"
            class="select-none outline-none bg-transparent text-gray-800"
            placeholder="Search"
            style="width: 17rem;"
          >
          <i class="fas fa-search mr-1 text-gray-400 cursor-default" />
        </div>

        <ul
          v-if="list.length > 0"
          tabindex="-1"
          role="listbox"
          aria-labelledby="listbox-label"
          aria-activedescendant="listbox-item-3"
          class="max-h-60 py-1 text-base leading-6 shadow-xs overflow-auto focus:outline-none sm:text-sm sm:leading-5"
        >
          <li
            v-for="(item, index) in results"
            id="listbox-item-0"
            :key="item.primaryText + index"
            role="option"
            class="text-gray-900 cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100"
            @click="selectItem(item)"
          >
            <div class="flex space-x-2">
              <span v-if="multiSelect">
                <Checkbox
                  :value="isSelected(item)"
                  :item="item"
                />
              </span>
              <span
                class="font-normal truncate"
                :class="multiSelect?'':'flex'"
              >
                <div>{{ item.primaryText }}</div>
                <div
                  class="text-gray-500 truncate"
                  :class="multiSelect?'':'ml-2'"
                >
                  {{ item.secondaryText }}
                </div>

                <span
                  v-if="!multiSelect && isSelected(item)"
                  class="text-gray-800 absolute inset-y-0 right-0 flex items-center pr-4"
                >
                  <svg
                    class="h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </span>
              </span>
            </div>
          </li>
        </ul>

        <div
          v-else
          class="text-gray-700 p-3 text-center text-sm"
        >
          No {{ listTypeName }}s found
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Checkbox from '@/app/shared/components/form/checkbox/checkbox.vue';

export default {

  components: {
    Checkbox,
  },

  props: {
    label: {
      type: String,
      default: '',
    },
    list: {
      type: Array,
      default() {
        return [];
      },
    },
    defaultItem: {
      type: Object,
      default: null,
    },
    selectDefault: {
      type: Boolean,
      default: true,
    },
    allowSearch: {
      type: Boolean,
      default: false,
    },
    multiSelect: {
      type: Boolean,
      default: false,
    },
    listTypeName: {
      type: String,
      default: 'item',
    },
  },

  data() {
    return {
      dropdownOpen: false,
      selectedItems: [],
      search: '',
    };
  },

  computed: {
    displaySelectedItem() {
      if (this.defaultItem === null) {
        return this.selectedItems;
      }
      return this.list.filter(i => i.id === this.defaultItem.id);
    },
    results() {
      if (this.search === '') {
        return this.list;
      }
      return this.list.filter(i => i.primaryText.toLowerCase().includes(this.search.toLowerCase()));
    },
  },

  watch: {
    list() {
      if (this.selectedItems.length === 0 && this.list.length > 0 && this.selectDefault) {
        this.selectedItems.push(this.list[0]);
      }
    },

    defaultItem() {
      this.selectedItems = [];
      if (this.defaultItem !== null) {
        this.selectedItems.push(this.defaultItem);
      }
    },
  },

  mounted() {
    if (this.defaultItem !== null) {
      this.selectedItems.push(this.defaultItem);
    }
  },

  methods: {
    selectItem(item) {
      if (this.multiSelect) {
        if (!this.isSelected(item)) {
          this.selectedItems.push(item);
        } else {
          this.removeItem(item);
        }
      } else {
        if (this.selectedItems[0] !== undefined) {
          this.selectedItems.splice(0, 1);
        }

        this.selectedItems.push(item);
        this.dropdownOpen = false;
      }

      this.$emit('select', this.selectedItems);
    },

    isSelected(item) {
      return this.selectedItems.includes(item) || item.id === this.defaultItem.id;
    },

    removeItem(item) {
      for (let i = 0; i < this.selectedItems.length; i += 1) {
        if (item === this.selectedItems[i]) {
          this.selectedItems.splice(i, 1);
        }
      }
    },
  },

};
</script>
